import { Box } from "@mui/material";
import { GridToolbarContainer } from "@mui/x-data-grid";
import { GridToolbarQuickFilter } from "@mui/x-data-grid";
import { motion } from "motion/react";
import { createElement } from "react";
import { useTranslation } from "react-i18next";
import { TbRefresh } from "react-icons/tb";

import CommonGridTable from "@/components/CommonGridTable";
import ExportTable from "@/components/ExportTable";
import FilterButton from "@/components/table/FilterButton";
import { DriverStatusColumnType, driverStatuses } from "@/constants/driverStatuses";
import { DRIVER_STATUS } from "@/constants/status";
import useDriversContext from "@/providers/data/DriversContext";
import { useExportDailyStatusList } from "@/requests/daily-status.queries";
import { ExportTypes } from "@/types/export.types";

import { useDriverStatusContext } from "../DriverStatusContext";
import DriverStatusTooltip from "../components/DriverStatusTooltip";
import { DriverColumnStatus } from "../types";
import { DriverStatusFilter } from "./components/DriverStatusFilter";
import DriverStatusVisibilityFilter from "./components/DriverStatusVisibilityFilter";

function CustomToolbar() {
    const { t } = useTranslation();

    const { mutateAsync, isPending: loading } = useExportDailyStatusList();
    const { month, hiddenDrivers, pagePermissions, reloadTable } = useDriverStatusContext();
    const { allDrivers } = useDriversContext();

    const handleRequest = async (format: ExportTypes): Promise<ArrayBuffer | undefined> => {
        if (loading) return;
        const startTime = month.startOf("month").toISO() as string;
        const endTime = month.endOf("month").toISO() as string;
        const statusTexts = Object.keys(DRIVER_STATUS).reduce<{ [key in DriverColumnStatus]: string }>(
            (prev, curr) => {
                prev[curr as DriverColumnStatus] = t("driverStatuses." + curr);

                return prev;
            },
            {} as { [key in DriverColumnStatus]: string }
        );
        const userIds = allDrivers.filter((item) => !hiddenDrivers.includes(item.id)).map((item) => item.id);
        const res = await mutateAsync({ endTime, startTime, format, ids: [], statusTexts, userIds });

        return res;
    };

    return (
        <GridToolbarContainer>
            <GridToolbarQuickFilter placeholder={t("nameOrPlate") + " ..."} />
            <Box sx={{ flexGrow: 1 }} />
            <DriverStatusVisibilityFilter />
            <FilterButton handleClick={() => reloadTable()} icon={TbRefresh} tooltip={t("tooltips.refreshTable")} />
            {pagePermissions.edit && (
                <ExportTable handleRequest={handleRequest} fileName="driverShiftInfos" loading={loading} />
            )}
        </GridToolbarContainer>
    );
}

function DriverStatusInfo({ item }: { item: DriverStatusColumnType }) {
    const { t } = useTranslation();

    return (
        <DriverStatusTooltip
            child={
                <motion.button
                    key={item.status}
                    whileHover={{ scale: 1.1, borderRadius: "10px" }}
                    whileTap={{ scale: 0.9 }}
                    style={{ backgroundColor: item.bg, color: item.hover }}
                    className={
                        "h-8 flex   items-center gap-1 px-2 py-1 text-xs font-medium  justify-center w-auto  rounded-sm shadow        cursor-pointer "
                    }
                >
                    {createElement(item.component(), { fontSize: 16, color: item.hover })}
                    {t("driverStatuses." + item.status)}
                </motion.button>
            }
            status={item}
        />
    );
}
function DriverStatusTable() {
    const { api, rows, columns, loading, fullScreen, allRows } = useDriverStatusContext();

    return (
        <>
            <div
                className={`${fullScreen ? "fixed inset-0 z-50 bg-white p-0 flex" : "flex  flex-col     w-full gap-2 h-full  "}`}
            >
                <div className=" flex gap-2 grow-0  shrink-0  justify-end w-full col-span-12 pl-5 ">
                    {driverStatuses.map((item) => (
                        <DriverStatusInfo key={item.status} item={item} />
                    ))}
                </div>
                <div className="col-span-12 flex w-full grow    overflow-auto  h-full  ">
                    <CommonGridTable
                        api={api}
                        rows={rows}
                        toolbar={CustomToolbar}
                        columns={columns}
                        loading={loading}
                        allRows={allRows}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: "userType", sort: "asc" }],
                            },
                            pinnedColumns: {
                                left: ["index", "name"],
                            },
                        }}
                        startElement={DriverStatusFilter}
                    />
                </div>
            </div>
        </>
    );
}

export default DriverStatusTable;
